import { useEffect, useRef, type RefObject } from 'react';

/** Trap only the active dialog; suspended parents retain focus restoration. */
export function useDialogFocus(
  ref: RefObject<HTMLElement | null>, isOpen: boolean, onClose: () => void, suspended = false,
) {
  const closeRef = useRef(onClose);
  const suspendedRef = useRef(suspended);
  closeRef.current = onClose;
  suspendedRef.current = suspended;
  useEffect(() => {
    if (!isOpen) return;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const frame = requestAnimationFrame(() => { if (!suspendedRef.current) ref.current?.focus(); });
    const handle = (event: KeyboardEvent) => {
      if (suspendedRef.current || !ref.current) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        closeRef.current();
      }
      if (event.key !== 'Tab') return;
      const items = Array.from(ref.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )).filter(element => element.offsetParent !== null);
      const first = items[0], last = items[items.length - 1];
      if (!first) { event.preventDefault(); ref.current.focus(); return; }
      if (!ref.current.contains(document.activeElement)
        || (event.shiftKey && (document.activeElement === first || document.activeElement === ref.current))) {
        event.preventDefault(); (event.shiftKey ? last : first).focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus();
      }
    };
    document.addEventListener('keydown', handle, true);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('keydown', handle, true);
      if (previous?.isConnected) previous.focus();
    };
  }, [ref, isOpen]);
}
